import React, {PureComponent} from 'react';
import styled from 'styled-components';
import Tree from 'react-sortable-tree';
import {NotificationManager} from 'react-notifications';

import {withUser} from '../../lib/user';
import * as api from '../../lib/api';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Screen from '../../components/Screen';
import Content from '../../components/Content';
import SubHeader from '../../components/SubHeader';
import Button from '../../components/Button';
import Placeholder from '../../components/Placeholder';
import Icon from '../../components/Icon';

import CatalogTypeForm from './CatalogTypeForm';
import CatalogItemForm from './CatalogItemForm';

import 'react-sortable-tree/style.css';
import './style.css';

class CatalogScreen extends PureComponent {
  state = {
    types: [],
    tree: [],
    loading: true,
    failed: false,
    catalogDialog: {
      open: false,
      parent: null,
      item: null
    },
    typeDialog: {
      open: false,
      type: null
    },
    expanded: {}
  };

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [catalog, types] = await Promise.all([
        api.getCatalog(credentials),
        api.getCatalogTypes(credentials)
      ]);

      this.setState({tree: this.buildCatalogTree(catalog), types});
    }
    catch(e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  openItemDialog = (item, parent) => {
    this.setState({catalogDialog: {open: true, loading: false, item, parent}});
  }

  closeItemDialog = () => {
    this.setState({catalogDialog: {open: false, loading: false, item: null, parent: null}});
  }

  openTypeDialog = type => {
    this.setState({typeDialog: {open: true, loading: false, type}});
  }

  closeTypeDialog = () => {
    this.setState({typeDialog: {open: false, loading: false, type: null}});
  }

  deleteItem = async item => {
    // eslint-disable-next-line
    if (!confirm(`Are you sure you want to delete the catalog item "${item.name}"?`)) {
      return;
    }

    const {credentials} = this.props;
    
    this.setState(state => ({
      tree: this.buildCatalogTree(flattenTree(state.tree).filter(i => i.id !== item.id))
    }));

    api.deleteCatalogItem(item, credentials);
  }

  handleExpandCollapse = ({ node, expanded }) => {
    this.setState(prevState => ({
      expanded: {
        ...prevState.expanded,
        [node.id]: expanded
      }
    }));
  }

  handleSave = async item => {
    const {credentials} = this.props;

    try {
      const method = item.tmp ? 'createCatalogItem' : 'editCatalogItem';
      item = await api[method](item, credentials);

      this.setState(state => ({
        tree: this.buildCatalogTree([
          ...flattenTree(state.tree).filter(i => i.id !== item.id),
          item
        ])
      }));
      this.closeItemDialog();
      return true;
    }
    catch(e) {
      NotificationManager.error('Unable to save catalog item. Please try again');
      return false;
    }
  }

  handleChangeTree = tree => {
    const {credentials} = this.props;
    const normalized = tree.map(root => normalizeTree(root));

    // Dispatch the update tree call, but optimistically ignore the result.
    api.organizeCatalog(flattenTree(normalized), credentials);
    this.setState({tree: normalized});
  }

  handleSaveType = async type => {
    const {credentials} = this.props;

    try {
      const method = type.tmp ? 'createCatalogType' : 'editCatalogType';
      type = await api[method](type, credentials);

      this.setState(state => ({
        types: [
          ...state.types.filter(t => t.id !== type.id),
          type
        ],
        tree: this.buildCatalogTree(flattenTree(state.tree).map(item => item.type.id === type.id ? {...item, type} : item))
      }));
      this.closeTypeDialog();
      return true;
    }
    catch(e) {
      NotificationManager.error('Unable to save catalog item. Please try again');
      return false;
    }
  }

  buildCatalogTree(catalog) {
    return catalog
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(item => !item.parentId).map(item => this.buildTreeFromNode(item, catalog));
  }
  
  buildTreeFromNode(item, catalog) {
    return {
      ...item,
      title: item.name,
      subtitle: item.type.name,
      expanded: this.state.expanded[item.id] || false,
      children: getChildren(item, catalog).map(child => this.buildTreeFromNode(child, catalog))
    };
  }

  renderCatalog() {
    const {loading, failed, tree, types, catalogDialog, typeDialog} = this.state;

    if(loading) {
      return <Loading fullScreen message="Loading catalog..." />;
    }

    if(failed) {
      return <Error fullScreen message="Unable to load the catalog" />;
    }

    return (
      <Content>
        <SubHeader options={<Button onClick={() => this.openTypeDialog()}>Add a Type</Button>}>Catalog Types</SubHeader>
        <Description>Catalog types are categories that you create to inventory the people, places, and things in your business.</Description>
        <CatalogSection>
          {types.length < 1 ? (
            <Placeholder height="6rem">There are no catalog types yet.</Placeholder>
          ) : types.sort((a, b) => a.name.localeCompare(b.name)).map(type => (
            <Chip key={type.id} onClick={() => this.openTypeDialog(type)}>{type.name}</Chip>
          ))}
        </CatalogSection>
        {types.length > 0 ? (
          <>
            <SubHeader options={<Button onClick={() => this.openItemDialog()}>Add an Item</Button>}>Catalog Tree</SubHeader>
            <Description>The catalog tree shows you a hierarchical view of the people, places, and things in your business. You can group items together for better insights in your reports.</Description>
            <CatalogSection>
              <Tree
                treeData={tree}
                onChange={this.handleChangeTree}
                onVisibilityToggle={this.handleExpandCollapse}
                rowHeight={80}
                isVirtualized={false}
                generateNodeProps={({node, path}) => ({
                  buttons: [
                    <Button blank block onClick={() => this.deleteItem(node)}>
                      <Icon name="trash-2" />
                    </Button>,
                    <Button blank block onClick={() => this.openItemDialog(node)}>
                      <Icon name="edit" />
                    </Button>,
                    <Button blank block onClick={() => this.openItemDialog(null, node)}>
                      <Icon name="plus" />
                    </Button>
                  ]
                })}
              />
            </CatalogSection>
          </>
        ) : null}
        <CatalogItemForm
          types={types}
          item={catalogDialog.item}
          parent={catalogDialog.parent}
          open={catalogDialog.open}
          onClose={this.closeItemDialog}
          onSave={this.handleSave}
        />
        <CatalogTypeForm
          types={types}
          type={typeDialog.type}
          open={typeDialog.open}
          onClose={this.closeTypeDialog}
          onSave={this.handleSaveType}
        />
      </Content>
    );
  }

  render() {
    return (
      <Screen title="Catalog" activeLink="Catalog">
        {this.renderCatalog()}
      </Screen>
    );
  }
}

const CatalogSection = styled.div`
  margin: 1.5rem 0 3rem 0;
`;

const Description = styled.p`
  margin: 0.5rem 0 0 0;
  color: #747474;
  line-height: 1.5;
`;

const Chip = styled(props => <Button blank {...props} />)`
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  background: #0049ee;
  color: #fff;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

function getChildren(item, catalog) {
  return catalog.filter(child => child.parentId === item.id);
}

function normalizeTree(tree, parentId = null) {
  return {
    ...tree,
    parentId,
    children: tree.children.map(child => normalizeTree(child, tree.id))
  };
}

function flattenTree(tree) {
  return tree
    .map(({children, ...item}) => {
      if(children.length < 1) {
        return [item];
      }

      return [item, ...flattenTree(children)];
    })
    .reduce((flatMap, next) => flatMap.concat(next), []);
}

export default withUser(CatalogScreen);
